<template>
  <section v-if="!isLoading" class="flex flex-col justify-center w-full">
    <div class="relative pb-7">
      <h1
        class="absolute top-7 left-0 right-0 bottom-0 text-orange-600 text-lg font-semibold text-center"
      >
        {{ translations.title }}
      </h1>
    </div>

    <div
      class="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 cursor-pointer shadow-md transition"
    >
      <BackSvg @click="$router.go(-1)" />
    </div>
    <!-- Card -->
    <div class="relative w-full max-w-lg mx-auto pt-2 mb-3">
      <div
        class="mb-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M13 16h-1v-4h-1m1-4h.01M12 22a10 10 0 100-20 10 10 0 000 20z"
          />
        </svg>

        <div class="flex-1">
          <p class="text-sm leading-6 text-amber-800">
            <span class="font-semibold">Note:</span>
            The information below shows your HMO plan benefits based on your
            coverage.
            <span class="font-semibold"
              >It does not represent your actual remaining or available benefit
              balance</span
            >.
          </p>

          <p class="mt-3 text-sm leading-6 text-amber-800">
            For inquiries regarding your remaining benefit balance, please
            contact the
            <span class="font-semibold">Customer Service Department (CSD)</span
            >.
          </p>
        </div>
      </div>

      <!-- Card Image -->
      <img
        src="@/assets/images/card.png"
        alt="IWC Card"
        class="w-full h-auto object-contain"
      />

      <!-- Text Overlay -->
      <div
        class="absolute top-[60%] left-[13%] flex flex-col leading-tight text-black font-card"
      >
        <h2
          class="text-base sm:text-lg md:text-xl uppercase font-bold tracking-widest"
        >
          {{ policyItems.iwccardno }}
        </h2>

        <h2 class="text-base sm:text-lg md:text-xl uppercase tracking-widest">
          {{ policyItems.membername }}
        </h2>

        <h2
          class="text-sm sm:text-base md:text-lg uppercase tracking-widest font-medium"
        >
          {{ policyItems.classification }}
        </h2>
      </div>
    </div>

    <!-- Status Badge -->
    <div class="text-center py-2" v-if="false">
      <span
        :class="[
          'inline-flex items-center px-3 py-1 rounded-full font-semibold text-sm',
          policyItems.status === 'Lapsed'
            ? 'bg-red-100 text-red-700'
            : 'bg-green-100 text-green-700',
        ]"
      >
        <!-- Dot -->
        <span
          :class="[
            'w-2 h-2 rounded-full mr-2',
            policyItems.status === 'Lapsed' ? 'bg-red-700' : 'bg-green-700',
          ]"
        ></span>

        {{ policyItems.status }}
      </span>
    </div>

    <!-- Accordion -->
    <div
      class="py-6 space-y-4 bg-white rounded-xl shadow-sm p-6 border border-gray-100 text-sm sm:text-base"
    >
      <div class="flex justify-between border-b pb-2">
        <p class="text-gray-600 font-medium">Annual Benefit Limit</p>
        <span class="font-semibold text-gray-900">
          {{ amountFormat(policyItems.maxcoverage) }}
        </span>
      </div>

      <div
        v-show="policyItems.illnessperyear"
        class="flex justify-between border-b pb-2"
      >
        <p class="text-gray-600 font-medium">Maximum Benefit Limit</p>
        <span class="font-semibold text-gray-900">
          {{ amountFormat(policyItems.illnessperyear) }}
        </span>
      </div>

      <div
        v-show="policyItems.outpatient"
        class="flex justify-between border-b pb-2"
      >
        <p class="text-gray-600 font-medium">Out-Patient</p>
        <span class="font-semibold text-gray-900">
          {{ amountFormat(policyItems.outpatient) }}
        </span>
      </div>

      <div
        v-show="policyItems.illnessperyear_op"
        class="flex justify-between border-b pb-2"
      >
        <p class="text-gray-600 font-medium">Out-Patient - Illness Per Year</p>
        <span class="font-semibold text-gray-900">
          {{ amountFormat(policyItems.illnessperyear_op) }}
        </span>
      </div>
      <div
        v-show="policyItems.semiannual_op"
        class="flex justify-between border-b pb-2"
      >
        <p class="text-gray-600 font-medium">Out-Patient Semi Annual</p>
        <span class="font-semibold text-gray-900">
          {{ amountFormat(policyItems.semiannual_op) }}
        </span>
      </div>

      <div
        v-show="policyItems.perquarter_op"
        class="flex justify-between border-b pb-2"
      >
        <p class="text-gray-600 font-medium">Out-Patient Per Quarter</p>
        <span class="font-semibold text-gray-900">
          {{ amountFormat(policyItems.perquarter_op) }}
        </span>
      </div>

      <div class="flex justify-between border-b pb-2">
        <p class="text-gray-600 font-medium">Room and Board</p>
        <span class="font-semibold text-gray-900">
          {{ policyItems.roomandboard }}
        </span>
      </div>

      <div
        class="flex justify-between border-b pb-2"
        v-show="policyItems.dentaltoavail === 'Covered'"
      >
        <p class="text-gray-600 font-medium">Dental</p>
        <span class="font-semibold text-gray-900">
          {{ translations.dental }}
        </span>
      </div>

      <div
        class="flex justify-between pb-2"
        v-show="policyItems.ape === 'Covered'"
      >
        <p class="text-gray-600 font-medium">APE</p>
        <span class="font-semibold text-gray-900">
          {{ translations.ape }}
        </span>
      </div>
    </div>
  </section>

  <div v-else>
    <div class="flex items-center justify-center h-48" v-if="isLoading">
      <svg
        class="w-10 h-10 animate-spin text-orange-600"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        ></path>
      </svg>
    </div>
  </div>
</template>

<script>
import reusableBase from "@/utils/composables/format";
import { inject, onMounted, ref, computed } from "vue";
import BackSvg from "@/components/svgs/BackSvg.vue";
import { useLanguage } from "@/utils/composables/useLanguage";
export default {
  name: "BaseAccordion",
  components: {
    BackSvg,
  },
  setup() {
    const { selectedLanguage } = useLanguage();
    const { amountFormat } = reusableBase();
    const activeIndex = ref(null);
    const memberStatus = ref("ACTIVE");
    const axios = inject("$axios");
    const policyItems = ref([]);
    const isLoading = ref(false);

    const toggle = (index) => {
      activeIndex.value = activeIndex.value === index ? null : index;
    };
    onMounted(async () => {
      try {
        isLoading.value = true;
        const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
        const { data } = await axios.get(
          `/meta/api/amap/resources/member/details`
        );
        await delay(2000);
        isLoading.value = false;
        policyItems.value = data.data[0];
        console.log(data.data[0]);
      } catch (error) {
        console.log("render", error);
      }
    });

    const translations = computed(
      () =>
        ({
          en: {
            title: "Plan Details",
            dental: "Covered",
            ape: "Covered up to APE Benefit",
          },
          ph: {
            title: "Impormasyon ng Benepisyo",
            dental: "Kabilang sa Benepisyo",
            ape: "Kasama sa benepisyo ng APE hanggang sa itinakdang limitasyon",
          },
        }[selectedLanguage.value])
    );

    return {
      memberStatus,
      activeIndex,
      toggle,
      policyItems,
      amountFormat,
      isLoading,
      selectedLanguage,
      translations,
    };
  },
};
</script>
